import React from 'react';

import {
  EmbedItemsFragment,
  useCreateEmbedItemMutation,
  useDeleteEmbedItemMutation,
  useEmbedQuery,
  useUpdateEmbedItemMutation,
} from '../../../generated/graphql';
import addItemToPage from '../../../helpers/apolloCache/addItemToPage';
import deletePageItem from '../../../helpers/apolloCache/deletePageItem';
import { useManageItem } from '../../../helpers/useManageItem';
import Layout from '../../FormComponents/Layout';
import { pagesQuery } from '../../LeftContainer/graphql';
import { PageItemProps } from '../BasicItem';
import Form, { InitialValues, initialValues, validationSchema } from './Form';

export const EmbedItem: React.FC<PageItemProps> = ({ onSaveHandler }) => {
  const { pageId, itemId, isNew, close } = useManageItem();
  const [create, { loading: creating }] = useCreateEmbedItemMutation();
  const [update, { loading: updating }] = useUpdateEmbedItemMutation();
  const [destroy, { loading: destroying }] = useDeleteEmbedItemMutation();

  const { data, loading } = useEmbedQuery({
    variables: {
      pageId,
      id: itemId!,
    },
    skip: isNew,
  });

  const item = data?.page?.embedItem;

  const deleteEmbed = async () => {
    if (itemId) {
      await destroy({
        variables: {
          id: itemId,
          pageId,
        },
        update(cache) {
          deletePageItem({ pageId, itemId, cache });
        },
        refetchQueries: [{ query: pagesQuery }],
      });
    }
  };

  const save = async (values: InitialValues): Promise<void> => {
    if (item?.id) {
      await update({
        variables: {
          attributes: {
            id: item.id,
            pageId: pageId,
            title: values.title || '',
            embededCode: values.embededCode,
            description: values.description || null,
          },
        },
        update: (_cache, { data }) => close(data?.updateEmbedItem),
      });
    } else {
      await create({
        variables: {
          attributes: {
            pageId: pageId,
            title: values.title || '',
            embededCode: values.embededCode || '',
            description: values.description || null,
          },
        },
        update: (cache, { data }) => {
          addItemToPage<EmbedItemsFragment | undefined>(cache, pageId, data?.createEmbedItem, close);
        },
        refetchQueries: [{ query: pagesQuery }],
      });
      close();
    }
    if (onSaveHandler) {
      onSaveHandler();
    }
  };

  return (
    <Layout
      headerText={`${isNew ? 'Add a' : 'Update'} Music Item`}
      validationSchema={validationSchema}
      initialValues={initialValues(item)}
      onSubmit={save}
      Component={Form}
      loading={loading}
      updating={updating || destroying || creating}
      onDelete={deleteEmbed}
      isNew={isNew}
      item={item}
      close={close}
      pageId={pageId}
    />
  );
};
